<template>
  <form @submit.prevent novalidate="true">
    <b-card>
      <template slot="header">
        <!-- Client Selection -->
        <div class="form-row">
          <div class="form-group col-md">
            <treeselect
              :value="clientValue"
              :clearable="false"
              :placeholder="$t('Webportal.ClientFilter.Placeholder')"
              :options="clientItems"
              :disableFuzzyMatching="true"
              :disabled="isBusy"
              @input="treeSelectInput('clientValue', 1, ...arguments)"
            />
          </div>
          <div class="form-group col-md">
            <treeselect
              :placeholder="$t('Webportal.CountryFilter.Placeholder')"
              :options="countryItems"
              :value="countryValue"
              :auto-load-root-options="false"
              :multiple="false"
              :searchable="true"
              :disableFuzzyMatching="true"
              :showCount="true"
              :flat="false"
              showCountOf="LEAF_DESCENDANTS"
              @input="treeSelectInput('countryValue', 2, ...arguments)"
            />
          </div>
        </div>
      </template>

      <!-- Spinner overlay -->
      <div v-bind:class="{ spinner: isLoading }" v-if="apisLoaded">
        <!-- Score selection -->
        <div>
          <b-row>
            <b-col sm="2">
              <span>Choose Score:</span>
            </b-col>
            <b-col sm="8">
              <treeselect
                placeholder="Score"
                :options="scores"
                :value="selectedScoreId"
                :disableFuzzyMatching="true"
                :disabled="!countryValue"
                @input="treeSelectInput('selectedScoreId', 3, ...arguments)"
              />
            </b-col>
            <b-col sm="2">
              <b-button block variant="danger" size="sm" :disabled="!selectedScoreId || selectedScoreId == -1" v-b-modal.deleteModal>
                Delete Score
              </b-button>
            </b-col>
          </b-row>
          <b-row class="my-3">
            <b-col sm="2">
              <span>Score Name:</span>
            </b-col>
            <b-col sm="8">
              <b-form-input 
                type="text" 
                v-model="selectedScoreName" 
                :disabled="disableNameInput"
              ></b-form-input>
            </b-col>
            <b-col sm="2">
              <b-button 
                block 
                variant="secondary" 
                size="sm" 
                :disabled="!selectedScoreId || !newScoreName"
                @click="setScore"
              >
                Change Name
              </b-button>
            </b-col>
          </b-row>
        </div>
        <div v-if="selectedScoreId">
          <!-- Score Threshold -->
          <div class="my-3">
            <ThresholdSelector 
              v-model="scoreThresholds"
              v-validate="'required'"
              data-vv-name="Thresholds" 
            />
            <hr />
          </div>
          <!-- APIs -->
          <div v-for="api in allApis" v-bind:key="`${api.Name}Container`" v-show="(api.Name != 'Seobility_de') && (api.Name != 'Seobility2_de')" >
            <div v-if="weightings.APIs[api.Name]">
              <!-- Logo / active switch -->
              <div class="form-group form-row">
                <label class="col-lg-4 col-md-6 col-form-label pt-0">
                  <b-card-img
                    style="width: 110px"
                    fluid
                    :src="api.PictureURL.includes('static') ? `/${api.PictureURL}` : `/static/${api.PictureURL}`"
                    :class="`${api.Name.toLowerCase()}-logo`"
                  />
                </label>
                <b-col>
                  <label class="switch switch-label switch-secondary">
                    <input
                      class="switch-input"
                      type="checkbox"
                      v-model="weightings.APIs[api.Name].isActive"
                      v-validate
                      :data-vv-name="`${api.Name}Active`"
                      :disabled="isBusy || editingDisabled"
                    />
                    <span
                      class="switch-slider"
                      data-checked="On"
                      data-unchecked="Off"
                    />
                  </label>
                </b-col>
              </div>

              <!-- Weight -->
              <div class="form-group form-row">
                <label class="col-lg-4 col-md-6 col-form-label pt-0 pl-3">
                  {{ $t("Webportal.CX.Administration.Weight") }}
                </label>
                <b-col>
                  <DxNumberBox
                    class="custom-control-inline"
                    width="6em"
                    height="2em"
                    :value.sync="weightings.APIs[api.Name].Weight"
                    v-validate
                    :data-vv-name="`${api.Name}Weight`"
                    @input="void 0"
                    format="##0"
                    :show-spin-buttons="true"
                    :step="1"
                    :min="0"
                    :max="999"
                    :disabled="isApiDisabled(api.Name)"
                  />
                </b-col>

                <label class="col-lg-4 col-md-6 col-form-label pt-0 pl-3">
                  {{ $t("Webportal.CX.Administration.Weight_NoData") }}
                </label>
                <b-col>
                  <DxNumberBox
                    class="custom-control-inline"
                    width="6em"
                    height="2em"
                    :value.sync="weightings.APIs[api.Name].Weight_NoData"
                    v-validate
                    :data-vv-name="`${api.Name}Weight_NoData`"
                    @input="void 0"
                    format="##0"
                    :show-spin-buttons="true"
                    :step="1"
                    :min="0"
                    :max="999"
                    :disabled="isApiDisabled(api.Name)"
                  />
                </b-col>
              </div>

              <!-- Config: NumberBox / StarRating | Slider -->
              <div class="form-group form-row" v-if="selectedScoreId == -1">
                <label class="col-lg-4 col-md-6 col-form-label pt-0 pl-3">
                  {{ $t("Webportal.CX.Administration.Threshold") }}
                </label>
                <b-col>
                  <b-row>
                    <b-col cols="2">
                      <DxNumberBox
                        class="custom-control-inline"
                        width="6em"
                        height="2em"
                        :value.sync="weightings.APIs[api.Name].Threshold"
                        v-validate
                        :data-vv-name="`${api.Name}Threshold`"
                        @input="void 0"
                        format="##0.##"
                        :show-spin-buttons="true"
                        :step="api.Stepping || 0.5"
                        :min="api.MinRating || 0"
                        :max="api.MaxRating || 5"
                        :disabled="isApiDisabled(api.Name)"
                      />
                    </b-col>
                    <b-col cols="8">
                      <!-- All non 5 star ratings are shown with slider -->
                      <!-- As the overlay looks weird on the slider, omit it -->
                      <DxSlider
                        v-if="(api.MaxRating || 5) != 5"
                        :step="api.Stepping || 0.01"
                        :min="api.MinRating || 0"
                        :max="api.MaxRating || 100"
                        :value.sync="weightings.APIs[api.Name].Threshold"
                        :tooltip="tooltip"
                        :disabled="isApiDisabled(api.Name)"
                      />

                      <b-overlay
                        v-else
                        :show="isApiDisabled(api.Name)"
                        variant="transparent"
                        :opacity="1"
                        style="z-index: 1"
                      >
                        <template v-slot:overlay>&nbsp;</template>
                        <star-rating
                          @rating-selected="weightings.APIs[api.Name].Threshold = $event"
                          :rating="weightings.APIs[api.Name].Threshold"
                          :star-size="40"
                          :padding="60"
                          :show-rating="false"
                          :fixed-points="1"
                          :increment="0.5"
                          :disabled="isApiDisabled(api.Name)"
                        />
                      </b-overlay>
                    </b-col>
                  </b-row>
                </b-col>
              </div>
              <hr />
            </div>
          </div>

          <!-- <hr /> -->

          <!-- KPIs -->
          <div v-if="selectedScoreId == -1">
            <p class="group-title">
              {{ $t("Webportal.Kpis") }}
            </p>
            <Accordion v-for="(group, group_index) in weightings.KPIGroups" :key="'KpiGroup_' + group_index" :toggleCols="1" :showHr="false" groupId="KPI" class="mb-4">
              <template slot="short" class="mb-n2">
                <h6>{{ group.KpiGroupName }}</h6>
              </template>

              <template slot="long">
                <b-row class="my-4 ml-2" v-for="(kpi, kpi_index) in weightings.KPIGroups[group_index].KPIs" :key="'KPI_' + kpi.KpiId">
                  <!-- Name -->
                  <b-col cols="5" class="pb-1" style="margin:auto">
                    {{ kpi.KpiName }}
                  </b-col>

                  <!-- IsActive -->
                  <b-col cols="1" class="mt-1" style="margin:auto !important">
                    <label class="switch switch-label switch-secondary">
                      <input
                        class="switch-input"
                        type="checkbox"
                        v-model="kpi.isActive"
                        v-validate
                        :data-vv-name="'KpiActive_' + kpi.KpiId"
                        :disabled="isBusy || editingDisabled"
                      />
                      <span
                        class="switch-slider"
                        data-checked="On"
                        data-unchecked="Off"
                      />
                    </label>
                  </b-col>

                  <!-- Threshold - Number box -->
                  <b-col cols="1" class="ml-n3 mr-2" v-if="!kpi.UseQuestionThreshold" >
                    <DxNumberBox
                      class="custom-control-inline"
                      width="6em"
                      height="2em"
                      :value.sync="kpi.Threshold"
                      v-validate
                      :data-vv-name="'KpiThreshold_' + kpi.KpiId"
                      format="##0.##"
                      :show-spin-buttons="true"
                      :step="0.01"
                      :min="0"
                      :max="100"
                      :disabled="isBusy || editingDisabled"
                      @value-changed="changeKpiValue(kpi, group, kpi_index, group_index, ...arguments)"
                    />
                  </b-col>

                  <!-- Threshold - Slider -->
                  <b-col v-if="!kpi.UseQuestionThreshold" >
                    <DxSlider
                      :min="0"
                      :max="100"
                      :value.sync="kpi.Threshold"
                      :tooltip="tooltip"
                      :step="0.01"
                      :disabled="isBusy || editingDisabled"
                      @value-changed="changeKpiValue(kpi, group, kpi_index, group_index, ...arguments)"
                    />
                  </b-col>
                  
                  <!-- QuestionThreshold -->
                  <b-col v-if="kpi.UseQuestionThreshold">
                    <span>QuestionThreshold (Minutes): </span>
                    <DxNumberBox
                      class="custom-control-inline"
                      width="6em"
                      height="2em"
                      :value.sync="kpi.QuestionThreshold"
                      v-validate
                      :data-vv-name="'KpiQuestionThreshold_' + kpi.KpiId"
                      format="##0.##"
                      :show-spin-buttons="true"
                      :step="1"
                      :min="0"
                      :max="518400"
                      :disabled="isBusy || editingDisabled"
                      @value-changed="changeKpiValue(kpi, group, kpi_index, group_index, ...arguments)"
                    />
                  </b-col>

                  <!-- ThresholdDirection -->
                  <b-col v-if="kpi.UseQuestionThreshold">
                    <span>ThresholdDirection: </span>
                    <select
                      v-model="kpi.ThresholdDirection"
                      v-validate
                      :data-vv-name="'KpiThresholdDirection_' + kpi.KpiId"
                      :disabled="isBusy || editingDisabled"
                      style="background:white; border-radius:4px; border:1px solid lightgrey"
                    >
                      <option value="BELOW">BELOW</option>
                      <option value="ABOVE">ABOVE</option>
                    </select>
                  </b-col>
                </b-row>
              </template>
            </Accordion>
            <hr />
          </div>

          <!-- Apis from Surveys -->
          <p class="group-title">Apis from Surveys</p>
          <div class="my-4 ml-2" v-for="(afs) in weightings.APIsFromSurveys" :key="afs.ExternalSurveyId">
            <!-- Name -->
            <b-row>
              <b-col cols="1" class="ml-2">
                <label class="switch switch-label switch-secondary">
                  <input
                    class="switch-input"
                    type="checkbox"
                    v-model="afs.isActive"
                    v-validate
                    :data-vv-name="'ApisFromSurveysActive_' + afs.ExternalSurveyId"
                    :disabled="isBusy || editingDisabled"
                  />
                  <span
                    class="switch-slider"
                    data-checked="On"
                    data-unchecked="Off"
                  />
                </label>
              </b-col>
              
              <b-col>
                <label class="col-lg-12 col-md-6 col-form-label pt-0" style="font-weight: bold">
                  {{ afs.Name }}
                </label>
              </b-col>
            </b-row>

            <!-- Weight -->
            <b-row class="form-group form-row">
              <label class="col-lg-4 col-md-6 col-form-label pt-0 pl-3">
                {{ $t("Webportal.CX.Administration.Weight") }}
              </label>
              <b-col>
                <DxNumberBox
                  class="custom-control-inline"
                  width="6em"
                  height="2em"
                  :value.sync="afs.Weight"
                  v-validate
                  :data-vv-name="'ApisFromSurveysWeight_' + afs.ExternalSurveyId"
                  @input="void 0"
                  format="##0"
                  :show-spin-buttons="true"
                  :step="1"
                  :min="0"
                  :max="999"
                  :disabled="isBusy || editingDisabled"
                />
              </b-col>

              <label class="col-lg-4 col-md-6 col-form-label pt-0 pl-3">
                {{ $t("Webportal.CX.Administration.Weight_NoData") }}
              </label>
              <b-col>
                <DxNumberBox
                  class="custom-control-inline"
                  width="6em"
                  height="2em"
                  :value.sync="afs.Weight_NoData"
                  v-validate
                  :data-vv-name="'ApisFromSurveysWeight_NoData_' + afs.ExternalSurveyId"
                  @input="void 0"
                  format="##0"
                  :show-spin-buttons="true"
                  :step="1"
                  :min="0"
                  :max="999"
                  :disabled="isBusy || editingDisabled"
                />
              </b-col>
            </b-row>

            <!-- Threshold -->
            <b-row class="form-group form-row" v-if="selectedScoreId == -1">
              <label class="col-lg-4 col-md-6 col-form-label pt-0 pl-3">
                {{ $t("Webportal.CX.Administration.Threshold") }}
              </label>
              <b-col>
                <b-row>
                  <b-col cols="2">
                    <DxNumberBox
                      class="custom-control-inline"
                      width="6em"
                      height="2em"
                      :value.sync="afs.Threshold"
                      v-validate
                      :data-vv-name="'ApisFromSurveysThreshold_' + afs.ExternalSurveyId"
                      @input="void 0"
                      format="##0.##"
                      :show-spin-buttons="true"
                      :step="0.01"
                      :min="0"
                      :max="100"
                      :disabled="isBusy || editingDisabled"
                    />
                  </b-col>
                  <b-col cols="8">
                    <DxSlider
                      :min="0"
                      :max="100"
                      :value.sync="afs.Threshold"
                      :tooltip="tooltip"
                      :step="0.01"
                      :disabled="isBusy || editingDisabled"
                    />
                  </b-col>
                </b-row>
              </b-col>
            </b-row>

            <hr />
          </div>
        </div>
      </div>

      <template slot="footer">
        <div class="float-right">
          <!-- <button type="button" @click="cancel" class="btn btn-secondary" :disabled="!isFormChanged || isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t('Webportal.Cancel') }}
          </button>-->

          <button
            type="button"
            @click="submit"
            class="btn btn-primary"
            v-if="!creatingNewScore"
            :disabled="!isFormChanged || !isFormValid || isBusy || editingDisabled"
          >
            <font-awesome-icon :icon="['far', 'dot-circle']" />
            {{ $t("Webportal.Save") }}
          </button>
          <button
            type="button"
            @click="setScore"
            class="btn btn-primary"
            v-if="creatingNewScore"
          >
            <font-awesome-icon :icon="['far', 'dot-circle']" />
            {{ $t("Webportal.Save") }}
          </button>
        </div>
      </template>
    </b-card>
    <b-modal id="deleteModal" hide-header hide-footer centered>
      <b-row>
        <b-col>
          <p style="text-align:center">
            Are you sure you want to delete "{{selectedScoreName}}"?
          </p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button variant="danger" block size="sm" @click="deleteScore()">Yes</b-button>
        </b-col>
        <b-col>
          <b-button variant="secondary" block size="sm" @click="$bvModal.hide('deleteModal')">No</b-button>
        </b-col>
      </b-row>
    </b-modal>
  </form>
</template>

<script>
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import CancelToken from "@/mixins/CancelToken";

import AdministationService from "@/services/AdministationService";
import DashboardService from "@/services/DashboardService";
import SurveysService from "@/services/SurveysService";

import ThresholdSelector from './ThresholdSelector';

import StarRating from "vue-star-rating";
import { DxSlider } from "devextreme-vue/slider";
import { DxNumberBox } from "devextreme-vue";

import Accordion from "../Accordion";

const DEFAULT_WEIGHTING = { isActive: false, Weight: 0, Weight_NoData: 0, Threshold: 0 };
const DEFAULT_WEIGHTINGS = {
  APIs: {
    "Google":         { ...DEFAULT_WEIGHTING, ApiID: 1, Name: "Google" },
    "Seobility_en":      { ...DEFAULT_WEIGHTING, ApiID: 2, Name: "Seobility_en" },
    "Seobility_de":      { ...DEFAULT_WEIGHTING, ApiID: 6, Name: "Seobility_de" },
    "Auto Scout 24":  { ...DEFAULT_WEIGHTING, ApiID: 3, Name: "Auto Scout 24" },
    "Mobile.de":      { ...DEFAULT_WEIGHTING, ApiID: 4, Name: "Mobile.de" },
    "Autocasion":     { ...DEFAULT_WEIGHTING, ApiID: 7, Name: "Autocasion" },
    "Marktplaats":    { ...DEFAULT_WEIGHTING, ApiID: 8, Name: "Marktplaats" },
    "Marktplaats":    { ...DEFAULT_WEIGHTING, ApiID: 9, Name: "facebook" },
    "Seobility2_en":      { ...DEFAULT_WEIGHTING, ApiID: 10, Name: "Seobility2_en" },
    "Seobility2_de":      { ...DEFAULT_WEIGHTING, ApiID: 11, Name: "Seobility2_de" }
  },
  KPIs: [],
  APIsFromSurveys: []
};

export default {
  name: "CXSettings",

  mixins: [AppConfig, CancelToken],

  components: {
    DxNumberBox,
    DxSlider,
    StarRating,
    Accordion,
    ThresholdSelector
  },

  data() {
    return {
      allApis: null,

      weightings: DEFAULT_WEIGHTINGS,
      
      clientItems: [],
      clientValue: null,
      countryItems: [],
      countryValue: null,
      scores: [],
      selectedScoreId: null,
      scoreThresholds: null,

      tooltip: {
        enabled: true,
        showMode: "always",
        position: "bottom",
        format: value => `${value}%`
      },

      isBusy: false,
      isLoading: false,
      apisLoaded: false,

      newScoreName: null
    };
  },

  mounted() {
    this.initializeTheme();
  },

  created() {
    this.getApis();
  },
  computed: {
    editingDisabled() {
      if (_.isNil(this.clientValue) || _.isNil(this.countryValue) || _.isNil(this.selectedScoreId)) {
        this.weightings = DEFAULT_WEIGHTINGS;
        return true;
      }

      this.getSettings();
      return false;
    },

    isFormChanged() {
      return Object.keys(this.validationFields).some(
        key => this.validationFields[key].dirty || this.validationFields[key].changed
      );
    },

    isFormValid() {
      return Object.keys(this.validationFields).every(
        key => (this.validationFields[key].valid || null) !== undefined
      );
    },

    selectedScoreName: {
      get: function() {
        if(this.creatingNewScore) {
          return this.newScoreName;
        }
        if (!this.selectedScoreId) return;
        let selectedScore = this.scores.find(score => score.id == this.selectedScoreId);
        return selectedScore.label
      },
      set: function(newVal) {
        if(this.creatingNewScore) {
          this.newScoreName = newVal;
          return;
        }
        if (!this.selectedScoreId) return;
        this.newScoreName = newVal;
        return;
        // let index = this.scores.findIndex(score => score.id == this.selectedScoreId);
        // this.scores[index].label = newVal;
      }
    },

    creatingNewScore() {
      if(this.selectedScoreId == "") {
        return true;
      }
      return false;
    },

    disableNameInput() {
      if(this.selectedScoreId == -1) {
        return true;
      } else if(this.creatingNewScore || !this.editingDisabled){
        return false;
      }
      return true;
    },

    seobilityEN() {
      return this.weightings.APIs.Seobility_en;
    },

    seobilityDE() {
      return this.weightings.APIs.Seobility_de;
    },

    seobility2EN() {
      return this.weightings.APIs.Seobility2_en;
    },

    seobility2DE() {
      return this.weightings.APIs.Seobility2_de;
    }
  },

  activated() {
    this.getSurveyStructure();
    // this.getCountries();
  },

  beforeRouteLeave(to, from, next) {
    this.$emit("unsavedChanges", this.isFormChanged, next);
  },
  watch: {
    selectedScoreId() {
      this.newScoreName = null;
    },
    seobilityEN: {
      handler () {
        this.seobilityDE.Threshold = this.seobilityEN.Threshold;
        this.seobilityDE.Weight = this.seobilityEN.Weight;
        this.seobilityDE.Weight_NoData = this.seobilityEN.Weight_NoData;
        this.seobilityDE.isActive = this.seobilityEN.isActive;
      },
      deep: true
    },
    seobility2EN: {
      handler () {
        this.seobility2DE.Threshold = this.seobility2EN.Threshold;
        this.seobility2DE.Weight = this.seobility2EN.Weight;
        this.seobility2DE.Weight_NoData = this.seobility2EN.Weight_NoData;
        this.seobility2DE.isActive = this.seobility2EN.isActive;
      },
      deep: true
    }
  },

  methods: {
    // Vue has problems with the nested structure of the KPIs for some reason..
    // This works that way in other places.. here we have to force an update.
    changeKpiValue(kpi, group, kpi_index, group_index, e) {
      //this.$set(group.KPIs, kpi_index, { ...kpi, Threshold: e.value });
      this.$forceUpdate();
    },

    isApiDisabled(name) {
      return this.isBusy || this.editingDisabled || !this.weightings?.APIs?.[name]?.isActive;
    },

    // Get survey structure data to extract all clients
    getSurveyStructure() {
      this.isLoading = true;

      return SurveysService.getSurveyStructure(null, true).then(response => {
        let structureItems = response.data;
        // Filter clients without children
        this.clientItems = _.map(structureItems, function(item) {
          if (_.startsWith(item.id, "C"))
            return { id: item.id.substring(1), label: item.label };
        });
        this.treeSelectInput('clientValue', 1, this.clientItems[0].id);
      })
      .finally(() => { this.isLoading = false; });
    },

    getCountries() {
      if (!this.clientValue) return;

      this.isLoading = true;
      const optionsCancelToken = this.initCancelToken(this.optionsCancelToken);

      return DashboardService.getClientLocationCountries(
          this.clientValue || AuthenticationService.localUserProfile.ClientId,
          optionsCancelToken
        )
        .then(response => {
          if (!response.data) return;

          this.countryItems = response.data.map(country => (
            {
              label: country.CountryName,
              id: country.CountryId
            }
          ));
        })
        .catch(error => console.error(error))
        .finally(() => { this.isLoading = false; });
    },

    getSettings() {
      if (!this.clientValue || !this.countryValue || !this.selectedScoreId) return;

      this.isLoading = true;
      this.isBusy = true;
      this.weightings = _.cloneDeep(DEFAULT_WEIGHTINGS);

      return AdministationService.getCXSettings({
          clientValue: this.clientValue,
          countryValue: this.countryValue,
          scoreId: this.selectedScoreId
        })
        .then(response => {
          if (!response.data) {
            console.warn('Got empty response for weightings.');
            return;
          }

          if (response.data.KPIGroups) {
            this.weightings.KPIGroups = response.data.KPIGroups.map(group =>
              ({
                ...group,
                KPIs: group.KPIs.map(kpi => _.defaults(kpi, { Threshold: 0, isActive: true }))
              })
            );
          }

          if (response.data.APIsFromSurveys) {
            this.weightings.APIsFromSurveys = response.data.APIsFromSurveys.map(afs =>
              _.defaults(afs, { isActive: false, Threshold: 0, Weight: 0, Weight_NoData: 0 })
            );
          }

          if (response.data.APIs) {
            _.merge(
              this.weightings.APIs,
              _.keyBy(response.data.APIs, api => {
                // Needed because Seobility_en resp. Seobility_de
                return api.Name; //.split("_")[0];
              })
            );
          }

          if (response.data.Thresholds) {
            this.scoreThresholds = response.data.Thresholds.map(x => { return { value: { startValue: x.Minimum, endValue: x.Maximum }, color: x.Color, icons: x.Icons }});
          } else {
            this.scoreThresholds = null;
          }

          this.$validator.reset();
        })
        .catch(error => console.warn("Error while load cx settings: " + error))
        .finally(() => {
          this.isLoading = false;
          this.isBusy = false;
        });
    },

    getScores() {
      if (!this.clientValue || !this.countryValue) return;

      this.isLoading = true;
      this.isBusy = true;

      AdministationService.getScores(this.clientValue, this.countryValue)
      .then((res) => {
        if (!res.data) {
          console.warn('Got empty response for scores.');
          return;
        } else {
          this.scores = res.data.map(score => (
            {
              label: score.ScoreName,
              id: score.ScoreId,
            }
          ));
          this.scores.push({
            label: "- Add new Score -",
            id: "",
          });
        }
      })
      .catch(error => console.warn("Error while load cx settings: " + error))
      .finally(() => {
        this.isLoading = false;
        this.isBusy = false;
      });
    },

    getApis() {
      AdministationService.getApis()
      .then(res => {
        if (!res.data) {
          console.warn('Got empty response for apis.');
          return;
        } else {
          this.allApis = res.data;
          this.apisLoaded = true;
        }
      })
    },

    // Called whenever the input of the structure treeselect changes
    treeSelectInput(valueField, step, value) {
      if (this[valueField] === value) return;

      let currentValue = this[valueField];
      this.$emit("unsavedChanges", this.isFormChanged, result => {
        // Cancel
        if (result === false) {
          this[valueField] = currentValue;
        }
        // User discarded changes or form is not dirty
        else if (result == null) {
          this[valueField] = value;

          switch (step) {
            case 1:
              this.getCountries();
              this.countryValue = null;
              this.scores = [];
              this.selectedScoreId = null;
              break;
            case 2:
              this.getScores();
              this.selectedScoreId = null;
              break;
            default:
              break;
          }
        }
      });
    },

    // cancel() {
    //   this.$emit("unsavedChanges", this.isFormChanged, result => {
    //     if (result !== false) {
    //       //this.getWeightings();
    //     }
    //   });
    // },

    submit() {

      this.isBusy = true;

      var settings = {
        clientValue: this.clientValue,
        countryValue: this.countryValue,
        ScoreId: this.selectedScoreId,
        APIs: _.map(this.weightings.APIs, api => { return api; }),
        KPIs: _.flatMap(this.weightings.KPIGroups, group => group.KPIs?.map(kpi => _.omit(kpi, "Name"))), // Omit "Name" property
        APIsFromSurveys: _.map(this.weightings.APIsFromSurveys, afs => _.omit(afs, "Name")), // Omit "Name" property
        ScoreThresholds: _.map(this.scoreThresholds, (thresholds) => { 
          return {
            Color: thresholds.color,
            ScoreId: this.selectedScoreId,
            Icons: thresholds.icons,
            Maximum: thresholds.value.endValue,
            Minimum: thresholds.value.startValue,
          }
        })
      }

      AdministationService.setCXSettings(settings)
      .then(response => {
        this.isBusy = false;
        this.$validator.reset();
      })
      .catch(e => {
        console.error("Failed to store settings.", e);
        this.$emit("saveError", result => {});
        this.isBusy = false;
      });
    },

    setScore() {
      this.isBusy = true;

      var score = {
        "ScoreId": this.selectedScoreId,
        "ClientId": this.clientValue,
        "CountryId": this.countryValue,
        "ScoreName": this.newScoreName
      }
      AdministationService.setScore(score)
      .then(() => {
        this.isBusy = false;
        this.$validator.reset();
        this.getScores();
        if(this.selectedScoreId == "") {
          this.selectedScoreId = null;
        }
      })
      .catch(e => {
        console.error("Failed to set score.", e);
        this.$emit("saveError", result => {});
        this.isBusy = false;
      });
    },

    deleteScore() {
      this.isBusy = true;
      AdministationService.deleteScore(this.selectedScoreId)
      .then(() => {
        this.isBusy = false;
        this.$validator.reset();
        this.$bvModal.hide('deleteModal')
        this.getScores();
        this.selectedScoreId = null;
      })
      .catch(e => {
        console.error("Failed to delete score.", e);
        this.$emit("saveError", result => {});
        this.isBusy = false;
      });
    }
  }
};
</script>

<style scoped>
.group-title {
  font-size: 1.3rem;
}
.seobility-logo {
  background-color: #327bb4;
}
</style>
